import React, { useState, useRef } from 'react';
import { Upload, X, File, FileText, Image, Video, Loader, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import FileViewer from './FileViewer';

const FileUpload = ({ attachments = [], onAttachmentsChange, taskId = null }) => {
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isDragActive, setIsDragActive] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const fileInputRef = useRef(null);

    const getFileIcon = (fileType) => {
        if (fileType.startsWith('image/')) {
            return <Image className="w-5 h-5 text-primary" />;
        } else if (fileType.startsWith('video/')) {
            return <Video className="w-5 h-5 text-purple-500" />;
        } else if (fileType === 'application/pdf') {
            return <FileText className="w-5 h-5 text-red-500" />;
        } else {
            return <File className="w-5 h-5 text-gray-500" />;
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const handleFiles = async (files) => {
        if (files.length === 0) return;

        // Validate file count (max 5 total)
        if (attachments.length + files.length > 5) {
            alert('Maximum 5 files allowed per task');
            return;
        }

        // Validate file sizes (max 10MB each)
        const invalidFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (invalidFiles.length > 0) {
            alert(`Some files exceed the 10 MB limit: ${invalidFiles.map(f => f.name).join(', ')}`);
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        try {
            const formData = new FormData();
            files.forEach(file => {
                formData.append('files', file);
            });

            const token = localStorage.getItem('token');
            const response = await api.post('/files/upload', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                },
            });

            if (response.data.success) {
                const newAttachments = [...attachments, ...response.data.files];
                onAttachmentsChange(newAttachments);
                toast.success(`${response.data.files.length} file(s) uploaded successfully`);
            }
        } catch (error) {
            console.error('File upload error:', error);
            toast.error(error.response?.data?.message || 'Failed to upload files. Please try again.');
        } finally {
            setUploading(false);
            setUploadProgress(0);
            if (fileInputRef.current) fileInputRef.current.value = ''; // Reset input
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        handleFiles(files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!uploading && attachments.length < 5) {
            setIsDragActive(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);

        if (uploading || attachments.length >= 5) return;

        const files = Array.from(e.dataTransfer.files);
        handleFiles(files);
    };

    const handleRemoveFile = async (apiFile) => {
        // If editing an existing task, delete from server immediately
        if (taskId) {
            const loadingToast = toast.loading('Deleting file...');
            try {
                const token = localStorage.getItem('token');
                await api.delete(`/tasks/${taskId}/attachment`, {
                    headers: { Authorization: `Bearer ${token}` },
                    data: { fileKey: apiFile.file_key }
                });

                // Remove from local state
                const newAttachments = attachments.filter(f => f.file_key !== apiFile.file_key);
                onAttachmentsChange(newAttachments);

                toast.success('File deleted successfully', { id: loadingToast });
            } catch (error) {
                console.error('File deletion error:', error);
                toast.error(error.response?.data?.message || 'Failed to delete file', { id: loadingToast });
            }
        } else {
            // Creating new task - just remove from list
            const newAttachments = attachments.filter(f => f.file_key !== apiFile.file_key);
            onAttachmentsChange(newAttachments);
            toast.success('File removed');
        }
    };

    return (
        <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-700">
                File Attachments (Optional)
            </label>

            {/* Drag and Drop Area */}
            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`
                    relative border-2 border-dashed rounded-xl p-6 transition-all cursor-pointer flex flex-col items-center gap-2 text-center
                    ${isDragActive
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    }
                    ${(uploading || attachments.length >= 5) ? 'opacity-50 cursor-not-allowed' : ''}
                `}
            >
                <div className="p-3 bg-primary/10 rounded-full text-primary">
                    <Upload className="w-6 h-6" />
                </div>
                <div>
                    <p className="text-sm font-medium text-gray-700">
                        {uploading ? 'Uploading files...' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        PDF, Word, Images, Video (max 10MB per file)
                    </p>
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp,.mp4,.webm"
                    onChange={handleFileSelect}
                    disabled={uploading || attachments.length >= 5}
                    className="hidden"
                />
            </div>

            {/* Upload Progress */}
            {uploading && (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Loader className="w-4 h-4 animate-spin text-primary" />
                        <span className="text-sm text-gray-600">Uploading... {uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Attached Files List */}
            {attachments.length > 0 && (
                <div className="space-y-2">
                    <p className="text-xs text-gray-600 font-medium">
                        Attached Files ({attachments.length}/5)
                    </p>
                    <div className="space-y-2">
                        {attachments.map((file, index) => (
                            <div
                                key={index}
                                className="p-3 bg-white border border-gray-200 rounded-lg group hover:shadow-sm transition-shadow"
                            >
                                <div className="flex items-center justify-between">
                                    <div
                                        className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                                        onClick={() => setPreviewFile(file)}
                                    >
                                        {getFileIcon(file.file_type)}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-800 truncate group-hover:text-primary transition-colors">
                                                {file.file_name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {formatFileSize(file.file_size)}
                                            </p>
                                        </div>
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity px-2">
                                            <Eye className="w-4 h-4 text-gray-400" />
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveFile(file);
                                        }}
                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                        title="Remove file"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Add to Documents checkbox */}
                                <div className="mt-2 ml-8 pl-0.5">
                                    <label className="flex items-start gap-2 cursor-pointer group/check">
                                        <input
                                            type="checkbox"
                                            checked={file.add_to_documents || false}
                                            onChange={(e) => {
                                                const updated = [...attachments];
                                                updated[index] = { ...updated[index], add_to_documents: e.target.checked };
                                                onAttachmentsChange(updated);
                                            }}
                                            className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                                        />
                                        <div>
                                            <span className="text-xs font-medium text-gray-700 group-hover/check:text-gray-900">
                                                Also add this file to employee documents
                                            </span>
                                            <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                                                This file will be added to each employee's document library when they're assigned this task.
                                            </p>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* File Viewer Modal */}
            <FileViewer
                file={previewFile}
                isOpen={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />
        </div>
    );
};

export default FileUpload;

